import { z } from "zod";
export const OPS_IMAGE_INPUT_MAX_BYTES = 12 * 1024 * 1024;
export const OPS_IMAGE_OUTPUT_MAX_BYTES = 12 * 1024 * 1024;
export const OPS_IMAGE_MAX_EDGE = 8_192;
export const OPS_IMAGE_MAX_PIXELS = 32 * 1024 * 1024;
export const OPS_IMAGE_MAX_EDITABLE_ARTIFACTS = 8;
export const OpsImageMimeTypeSchema = z.enum(["image/png", "image/jpeg", "image/webp"]);
export const OpsImageOutputFormatSchema = z.enum(["png", "jpeg", "webp", "auto"]);
export const OpsImageQualitySchema = z.enum(["low", "medium", "high", "auto"]);
export const OpsImageSizeSchema = z.enum(["1024x1024", "1536x1024", "1024x1536", "auto"]);
export const OpsImageBackgroundSchema = z.enum(["opaque", "transparent", "auto"]);
export const OpsImageUpscaleFactorSchema = z.union([z.literal(2), z.literal(4)]);
export const OpsImageOutputOptionsSchema = z.object({
    size: OpsImageSizeSchema,
    quality: OpsImageQualitySchema,
    format: OpsImageOutputFormatSchema,
    background: OpsImageBackgroundSchema
}).strict().superRefine((value, context) => {
    // WHY R613: native Codex 0.155.1 has no size/quality/format knobs.
    // A partial AUTO tuple would invent a guarantee its tool cannot honor.
    if (Object.values(value).includes("auto") && !Object.values(value).every((entry) => entry === "auto")) {
        context.addIssue({ code: "custom", message: "Native automatic output requires all settings to be auto." });
    }
    if (value.background === "transparent" && value.format === "jpeg") {
        context.addIssue({
            code: "custom",
            path: ["format"],
            message: "Transparent image output must use PNG or WebP."
        });
    }
});
export const OpsImageOutputContractSchema = z.array(OpsImageOutputOptionsSchema)
    .min(1)
    .max(64)
    .refine((outputs) => new Set(outputs.map((output) => JSON.stringify(output))).size === outputs.length, {
    message: "Image output choices must be unique."
});
const OpsImageToolCallBaseSchema = z.object({
    prompt: z.string().trim().min(1).max(4_000),
    output: OpsImageOutputOptionsSchema
}).strict();
export const OpsImageGenerateToolCallSchema = OpsImageToolCallBaseSchema.extend({
    operation: z.literal("GENERATE"),
    sourceEventId: z.null(),
    sourceArtifactId: z.null().optional()
}).strict();
export const OpsImageEditUploadToolCallSchema = OpsImageToolCallBaseSchema.extend({
    operation: z.literal("EDIT"),
    sourceEventId: z.uuid(),
    sourceArtifactId: z.null().optional()
}).strict();
export const OpsImageEditArtifactToolCallSchema = OpsImageToolCallBaseSchema.extend({
    operation: z.literal("EDIT"),
    sourceEventId: z.null(),
    sourceArtifactId: z.uuid()
}).strict();
/** Model-selected image work is a closed tool call. The model may choose an
 * already supplied source-event ID, but never a URL, path, provider, model, or
 * storage key. Current server state pins those authorities into the effect. */
export const OpsImageToolCallSchema = z.union([
    OpsImageGenerateToolCallSchema,
    OpsImageEditUploadToolCallSchema,
    OpsImageEditArtifactToolCallSchema
]);
export const OpsImagePinnedSourceSchema = z.object({
    sourceEventId: z.uuid(),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    mimeType: OpsImageMimeTypeSchema,
    byteCount: z.int().positive().max(OPS_IMAGE_INPUT_MAX_BYTES)
}).strict();
export const OpsImagePinnedArtifactSchema = z.object({
    sourceArtifactId: z.uuid(),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    mimeType: OpsImageMimeTypeSchema,
    byteCount: z.int().positive().max(OPS_IMAGE_OUTPUT_MAX_BYTES)
}).strict();
export const OpsImageEditableArtifactSchema = OpsImagePinnedArtifactSchema.extend({
    width: z.int().positive().max(OPS_IMAGE_MAX_EDGE),
    height: z.int().positive().max(OPS_IMAGE_MAX_EDGE),
    createdAt: z.iso.datetime()
}).strict().superRefine((value, context) => {
    if (value.width * value.height > OPS_IMAGE_MAX_PIXELS) {
        context.addIssue({ code: "custom", path: ["width"], message: "The image exceeds the pixel budget." });
    }
});
/** Request-scoped image capability advertised to the semantic owner. It lists
 * only provider-supported output tuples and immutable retained artifacts that
 * the server will accept as EDIT parents. */
export const OpsImageTurnBindingSchema = z.object({
    supportedOutputs: OpsImageOutputContractSchema,
    editableArtifacts: z.array(OpsImageEditableArtifactSchema).max(OPS_IMAGE_MAX_EDITABLE_ARTIFACTS)
}).strict();
const OpsImageEffectAuthoritySchema = z.object({
    effectId: z.uuid(),
    workspaceId: z.uuid(),
    actorId: z.uuid(),
    requestKey: z.string().trim().min(1).max(160).regex(/^[A-Za-z0-9._:-]+$/),
    createdAt: z.iso.datetime(),
    version: z.literal(1)
}).strict();
export const OpsImageGenerateEffectSchema = OpsImageEffectAuthoritySchema.extend({
    operation: z.literal("GENERATE"),
    prompt: z.string().trim().min(1).max(4_000),
    source: z.null(),
    sourceArtifactId: z.null(),
    upscaleFactor: z.null(),
    output: OpsImageOutputOptionsSchema
}).strict();
export const OpsImageEditUploadEffectSchema = OpsImageEffectAuthoritySchema.extend({
    operation: z.literal("EDIT"),
    prompt: z.string().trim().min(1).max(4_000),
    source: OpsImagePinnedSourceSchema,
    sourceArtifactId: z.null(),
    upscaleFactor: z.null(),
    output: OpsImageOutputOptionsSchema
}).strict();
export const OpsImageEditArtifactEffectSchema = OpsImageEffectAuthoritySchema.extend({
    operation: z.literal("EDIT"),
    prompt: z.string().trim().min(1).max(4_000),
    source: z.null(),
    sourceArtifactId: OpsImagePinnedArtifactSchema,
    upscaleFactor: z.null(),
    output: OpsImageOutputOptionsSchema
}).strict();
export const OpsImageUpscaleEffectSchema = OpsImageEffectAuthoritySchema.extend({
    operation: z.literal("UPSCALE"),
    prompt: z.null(),
    source: z.null(),
    sourceArtifactId: z.uuid(),
    upscaleFactor: OpsImageUpscaleFactorSchema,
    output: z.null()
}).strict();
export const OpsImageEffectSchema = z.union([
    OpsImageGenerateEffectSchema,
    OpsImageEditUploadEffectSchema,
    OpsImageEditArtifactEffectSchema,
    OpsImageUpscaleEffectSchema
]);
export const OpsImageUsageSchema = z.object({
    inputTokens: z.int().nonnegative(),
    outputTokens: z.int().nonnegative(),
    totalTokens: z.int().nonnegative()
}).strict().superRefine((value, context) => {
    if (value.totalTokens !== value.inputTokens + value.outputTokens) {
        context.addIssue({ code: "custom", path: ["totalTokens"], message: "Image token totals must reconcile." });
    }
});
export const OpsImageProvenanceSchema = z.object({
    provider: z.enum(["OPENAI_IMAGE_API", "CODEX_IMAGE", "LOCAL_UPSCALER"]),
    model: z.string().trim().min(1).max(120),
    providerRequestId: z.string().trim().min(1).max(200).nullable(),
    // Internal only: app-server skips backend IDs. Its item ID is a tool-call
    // ID, never a fabricated backend request ID. Public projection stays exact.
    nativeToolCallId: z.string().trim().min(1).max(200).optional(),
    actorId: z.uuid(),
    requestKey: z.string().trim().min(1).max(160).regex(/^[A-Za-z0-9._:-]+$/),
    costReservationMicros: z.int().nonnegative().max(1_000_000_000),
    promptSha256: z.string().regex(/^[0-9a-f]{64}$/).nullable(),
    sourceEventId: z.uuid().nullable(),
    sourceArtifactId: z.uuid().nullable(),
    sourceSha256: z.string().regex(/^[0-9a-f]{64}$/).nullable(),
    requestedOutput: OpsImageOutputOptionsSchema.nullable(),
    upscaleFactor: OpsImageUpscaleFactorSchema.nullable(),
    inputHash: z.string().regex(/^[0-9a-f]{64}$/),
    usage: OpsImageUsageSchema.nullable(),
    startedAt: z.iso.datetime(),
    completedAt: z.iso.datetime()
}).strict();
export const OpsImageArtifactSchema = z.object({
    id: z.uuid(),
    workspaceId: z.uuid(),
    effectId: z.uuid(),
    operation: z.enum(["GENERATE", "EDIT", "UPSCALE"]),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    mimeType: OpsImageMimeTypeSchema,
    byteCount: z.int().positive().max(OPS_IMAGE_OUTPUT_MAX_BYTES),
    width: z.int().positive().max(OPS_IMAGE_MAX_EDGE),
    height: z.int().positive().max(OPS_IMAGE_MAX_EDGE),
    createdAt: z.iso.datetime(),
    provenance: OpsImageProvenanceSchema
}).strict().superRefine((value, context) => {
    if (value.width * value.height > OPS_IMAGE_MAX_PIXELS) {
        context.addIssue({ code: "custom", path: ["width"], message: "The image exceeds the pixel budget." });
    }
});
export const OpsImageArtifactProjectionSchema = z.object({
    artifactId: z.uuid(),
    altText: z.string().trim().min(1).max(240),
    mimeType: OpsImageMimeTypeSchema,
    byteCount: z.int().positive().max(OPS_IMAGE_OUTPUT_MAX_BYTES),
    width: z.int().positive().max(OPS_IMAGE_MAX_EDGE),
    height: z.int().positive().max(OPS_IMAGE_MAX_EDGE),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    provider: z.enum(["OPENAI_IMAGE_API", "CODEX_IMAGE", "LOCAL_UPSCALER"]),
    model: z.string().trim().min(1).max(120),
    providerRequestId: z.string().trim().min(1).max(200).nullable(),
    sourceArtifactId: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
    availableUpscales: z.array(OpsImageUpscaleFactorSchema).max(2).refine((values) => new Set(values).size === values.length, { message: "Upscale choices must be unique." })
}).strict().superRefine((value, context) => {
    for (const factor of value.availableUpscales) {
        if (value.width * factor > OPS_IMAGE_MAX_EDGE
            || value.height * factor > OPS_IMAGE_MAX_EDGE
            || value.width * value.height * factor * factor > OPS_IMAGE_MAX_PIXELS) {
            context.addIssue({
                code: "custom",
                path: ["availableUpscales"],
                message: `${factor}x exceeds the image processing budget.`
            });
        }
    }
});
export const OpsImageFailureCodeSchema = z.enum([
    "EFFECT_CONFLICT",
    "RATE_LIMITED",
    "COST_LIMITED",
    "SOURCE_UNAVAILABLE",
    "SOURCE_CHANGED",
    "SOURCE_TYPE_UNSUPPORTED",
    "OUTPUT_UNSUPPORTED",
    "SOURCE_TOO_LARGE",
    "ARTIFACT_UNAVAILABLE",
    "UPSCALE_NOT_CONFIGURED",
    "PROVIDER_TIMEOUT",
    "PROVIDER_FAILED",
    "PROVIDER_OUTPUT_INVALID",
    "PROVIDER_OUTPUT_TOO_LARGE",
    "ARTIFACT_STORE_FAILED",
    "LEDGER_FINALIZATION_FAILED"
]);
export const OpsImageJobProjectionSchema = z.discriminatedUnion("state", [
    z.object({
        state: z.literal("PENDING"),
        effectId: z.uuid(),
        artifact: z.null(),
        failureCode: z.null(),
        message: z.string().trim().min(1).max(240)
    }).strict(),
    z.object({
        state: z.literal("PROCESSING"),
        effectId: z.uuid(),
        artifact: z.null(),
        failureCode: z.null(),
        message: z.string().trim().min(1).max(240)
    }).strict(),
    z.object({
        state: z.literal("FAILED"),
        effectId: z.uuid(),
        artifact: z.null(),
        failureCode: OpsImageFailureCodeSchema,
        message: z.string().trim().min(1).max(240)
    }).strict(),
    z.object({
        state: z.literal("SUCCEEDED"),
        effectId: z.uuid(),
        artifact: OpsImageArtifactProjectionSchema,
        failureCode: z.null(),
        message: z.string().trim().min(1).max(240)
    }).strict()
]);
//# sourceMappingURL=ops-image.js.map